import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  ChevronDown, 
  ArrowRight,
  TrendingUp,
  TrendingDown,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { User } from '@/api/entities';
import { useReserveEligibility } from '@/hooks/useReserveEligibility';

export default function ReserveLanding() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  // Defera Reserve was discontinued for new users. The /reserve marketing
  // page is now reserved for existing customers with a wallet balance (or
  // funds allocated to an active Reserve trip). Anyone else gets bounced
  // to the marketing home page.
  const { loading: isReserveEligibilityLoading, isEligible: isReserveEligible } = useReserveEligibility();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const loadUser = async () => {
      try {
        const u = await User.getCurrentUser();
        setUser(u);
      } catch (error) {
        setUser(null);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (isReserveEligibilityLoading) return;
    if (!isReserveEligible) {
      navigate('/', { replace: true });
    }
  }, [isReserveEligibilityLoading, isReserveEligible, navigate]);

  const handleStartReserve = () => {
    navigate('/app/reserve');
  };

  if (isReserveEligibilityLoading || !isReserveEligible) {
    return <div className="min-h-screen bg-black" />;
  }

  const scrollToLearnMore = () => {
    document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' });
  };

  const howItWorks = [
    {
      step: '01',
      title: 'Find Your Flight',
      description: 'Search for flights and select your itinerary. Add it to your Reserve with no large deposit required.'
    },
    {
      step: '02',
      title: 'Start Your Payments',
      description: 'Begin making payments on your own schedule. All payments wrap up 4 weeks before departure. No interest, no credit checks.'
    },
    {
      step: '03',
      title: 'Watch & Wait',
      description: 'Track live prices while you pay. If price drops, you pay less. If it goes up, switch to another flight—no penalty.'
    },
    {
      step: '04',
      title: 'Ticket When Fully Paid',
      description: 'Ticket your flight once your plan is fully paid, or top up at any time to expedite. If the price dropped, the difference stays in Reserve for your next trip. Passengers can be selected when you\'re ready to ticket; no need to lock that in now.'
    }
  ];

  const faqs = [
    {
      question: 'What is Defera Reserve?',
      answer: 'Defera Reserve lets you start paying for a trip on a schedule before you ticket. Select a flight, make payments over time, and ticket when your plan is fully paid or you top off to expedite. If the price goes up, switch flights. If it goes down, you keep the savings.'
    },
    {
      question: 'What is Reserve good for?',
      answer: "Reserve works well when you want flexibility. Example: You're planning a wedding trip in six months—set up a weekly plan, let it build, and ticket when you're ready. If the fare drops before then, you pay less. Example: You're eyeing a flight but not sure about dates yet—add money to Reserve and apply it when you pick your trip. Example: You have variable income—top up when you can, and use a plan for steady progress. Reserve keeps your options open until you ticket."
    },
    {
      question: 'How does Reserve protect me from fare hikes?',
      answer: 'When you start paying toward a trip with Reserve, you\'re not locked in to that price. If the fare increases before you ticket, simply switch to a different flight that fits your budget. Your payments stay flexible until you decide to book.'
    },
    {
      question: 'What happens if the price goes down?',
      answer: 'You win! If the fare drops before you ticket, you only pay the lower price. The difference remains in your Reserve balance for future travel. There\'s no downside.'
    },
    {
      question: 'What if the trip price went up by the time I ticket?',
      answer: "Reserve prices stay live until you ticket, so fares can change. If the fare went up, you'll see the difference at checkout. You can pay the extra amount with your card to complete the booking, or switch to a different flight if you prefer."
    },
    {
      question: 'Can I switch flights after I start paying?',
      answer: 'Absolutely. Until you ticket, your Reserve balance can be applied to any flight on Defera. Changed your mind about the destination? Found a better deal? Just switch—no fees, no penalties.'
    },
    {
      question: 'How do payments work?',
      answer: 'Your trip total is split into equal payments — no large deposits. Your first payment is due at checkout, and the remaining payments follow your chosen schedule (weekly, biweekly, or monthly). All installments are completed 4 weeks before departure so you have time to ticket. You can also pay ahead of schedule if you prefer.'
    },
    {
      question: 'Which trips are eligible for Reserve?',
      answer: 'Reserve is available for trips departing at least 2 months from today. This ensures there\'s enough time to set up a comfortable payment schedule. All payments conclude 4 weeks before your departure date. You can ticket when your plan is fully paid or top off to expedite.'
    },
    {
      question: 'Is my fare locked when I start paying?',
      answer: 'No, prices remain live until you ticket. Reserve gives you flexibility without the risk. You\'re making payments toward a trip, not committing to a price. This is what makes Reserve powerful—you\'re protected either way.'
    },
    {
      question: 'Can I withdraw my Reserve funds?',
      answer: 'Reserve funds are designed for travel on Defera. While direct withdrawals aren\'t available, your balance never expires and can be applied to flights, hotels, and other travel services we offer.'
    },
    {
      question: 'What payment methods can I use?',
      answer: 'You can make payments using any major credit or debit card. We securely store your cards for easy scheduled payments.'
    },
    {
      question: 'When do I add passenger details?',
      answer: 'Passengers can be selected once you\'re ready to ticket. There\'s no need to lock in passenger details now—add them when you complete your booking.'
    },
    {
      question: "What's the difference between Defera Reserve and Defera Zero?",
      answer: "Defera Zero is 0% down on refundable fares—your trip price is secured at booking, protected from future airfare increases while you're in good standing, and you pay over time with no payment due until your first installment date. Cancel up to 24 hours before departure and receive 75% of payments back. Defera Reserve is different: you pay toward a trip before ticketing, and the price stays live until you ticket. If the fare goes up, you can switch flights. If it goes down, you pay less. Reserve gives you flexibility; Defera Zero gives you locked-in pricing with nothing due today."
    }
  ];

  return (
    <>

      <div className="min-h-screen bg-black text-white">
        {/* Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="/defera-logo-web.png"
                alt="Defera" 
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate('/app/reserve')}
                  className="bg-white text-black hover:bg-white/90 rounded-full px-6"
                >
                  Go to Reserve
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/home?auth_mode=login')}
                    className="text-white/70 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={handleStartReserve}
                    className="bg-white text-black hover:bg-white/90 rounded-full px-6"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-screen flex flex-col pt-32 px-6 md:px-8 lg:px-10 bg-black relative">
          
          {/* Main Content Grid */}
          <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
            
            {/* Left Column: Headline */}
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <span className="font-mono text-xs text-white/40 tracking-widest uppercase">
                    Defera Reserve
                  </span>
                </div>
                
                <h1 className="text-6xl md:text-8xl lg:text-9xl font-light tracking-tighter leading-[0.9] text-white mb-8">
                  Total<br />
                  <span className="text-white/40">Travel</span><br />
                  Flexibility.
                </h1>
              </motion.div>
            </div>

            {/* Right Column: Description & Actions */}
            <div className="lg:col-span-4 flex flex-col justify-end">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="lg:pl-8 lg:border-l border-white/10"
              >
                <p className="text-xl text-white/70 font-light leading-relaxed mb-8">
                  Start paying for your trip today. Ticket when fully paid or top off to expedite. 
                  <span className="text-white block mt-4">
                    Price goes up? Switch flights.<br />
                    Price goes down? Keep the savings.<br />
                    Change your mind? Cancel anytime.
                  </span>
                </p>

                <div className="flex flex-col gap-4">
                  <Button 
                    onClick={handleStartReserve}
                    className="w-full bg-white text-black hover:bg-white/90 h-14 text-lg rounded-none border border-transparent"
                  >
                    Start Reserve
                    <ArrowRight className="w-5 h-5 ml-auto" />
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={scrollToLearnMore}
                    className="w-full justify-between h-14 text-lg font-light text-white/60 hover:text-white hover:bg-white/5 rounded-none border border-white/10"
                  >
                    How it works
                    <ChevronDown className="w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Cinematic Image Strip */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex-1 w-full max-w-7xl mx-auto min-h-[55vh] md:min-h-[300px] rounded-t-3xl overflow-hidden mt-auto"
          >
             <img 
               src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=80"
               alt="Group of friends laughing"
               className="absolute inset-0 w-full h-full object-cover opacity-60 grayscale-[20%]"
             />
             <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
             
             {/* Floating Badge */}
             <div className="absolute bottom-8 left-8 bg-black/50 backdrop-blur-md border border-white/10 px-4 py-2 flex items-center gap-3 rounded-full">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-white/80 font-normal">Zero risk. Total flexibility.</span>
             </div>
          </motion.div>

        </section>

        {/* Protection Section */}
        <section id="benefits" className="py-28 px-6 md:px-8 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <p className="text-white/40 text-sm tracking-widest uppercase mb-4">How You're Protected</p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight">
                Every scenario covered.
              </h2>
            </motion.div>

            {/* Grid Layout */}
            <div className="grid md:grid-cols-3 gap-6">
              
              {/* Price Goes Up */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-8 border-t border-white/20 hover:border-brand-400/50 transition-colors group"
              >
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-5 h-5 text-brand-400 group-hover:scale-110 transition-transform" />
                  <span className="text-white/40 text-xs font-normal uppercase tracking-wider">Price Up</span>
                </div>
                <h3 className="text-2xl font-light text-white mb-4">
                  Switch flights
                </h3>
                <p className="text-white/60 text-sm font-normal leading-relaxed">
                  Your funds aren't locked. If the price jumps, simply pick an alternative that fits your budget.
                </p>
              </motion.div>

              {/* Price Goes Down */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-8 border-t border-white/20 hover:border-emerald-400/50 transition-colors group"
              >
                <div className="flex items-center gap-3 mb-6">
                  <TrendingDown className="w-5 h-5 text-emerald-400 group-hover:scale-110 transition-transform" />
                  <span className="text-white/40 text-xs font-normal uppercase tracking-wider">Price Down</span>
                </div>
                <h3 className="text-2xl font-light text-white mb-4">
                  Keep the difference
                </h3>
                <p className="text-white/60 text-sm font-normal leading-relaxed">
                  Pay only the lower price. The savings stay in your Reserve for your next trip.
                </p>
              </motion.div>

              {/* Plans Change */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-8 border-t border-white/20 hover:border-brand-400/50 transition-colors group"
              >
                <div className="flex items-center gap-3 mb-6">
                  <RefreshCw className="w-5 h-5 text-brand-400 group-hover:rotate-180 transition-transform duration-500" />
                  <span className="text-white/40 text-xs font-normal uppercase tracking-wider">Plans Change</span>
                </div>
                <h3 className="text-2xl font-light text-white mb-4">
                  Apply to any trip
                </h3>
                <p className="text-white/60 text-sm font-normal leading-relaxed">
                  Change your mind? Cancel anytime. Total flexibility until you ticket—change destinations, dates, or airlines with no penalties.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Visual Divider with Image */}
        <section className="py-8 px-6 md:px-8 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[3/1]">
              <img 
                src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1600&q=80"
                alt="Travel"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-6 md:px-8 lg:px-10 bg-white/5">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-400 font-mono text-xs tracking-widest uppercase">No large deposits</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-light tracking-tight mb-6 leading-none">
                Reserve your trip.<br />
                <span className="text-white/40">No large deposits.</span>
              </h2>
              <p className="text-white/60 font-light text-xl max-w-2xl leading-relaxed">
                Split your trip into equal payments with no large deposits. Make payments on your schedule. Ticket when fully paid or top off to expedite.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative p-6"
                >
                  <div className="text-5xl font-light text-white/10 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-light mb-2">{item.title}</h3>
                  <p className="text-white/60 font-normal text-sm leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="py-32 px-6 md:px-8 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-white/30 text-sm tracking-widest uppercase mb-6">The Bottom Line</p>
              <h2 className="text-4xl md:text-6xl font-light tracking-tight mb-8 leading-tight">
                The only way to lose<br />
                <span className="text-white/40">is not to play.</span>
              </h2>
              <p className="text-white/60 font-light text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
                Traditional booking locks you into a price. Reserve doesn't. You're either paying less when fares drop, or switching when they rise. The math always works in your favor.
              </p>
              
              {/* Minimal feature list */}
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-white/80 text-sm font-normal">Fare protection</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-white/80 text-sm font-normal">Price drop savings</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-white/80 text-sm font-normal">Flexible switching</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section with Wide Image */}
        <section className="py-24 px-6 md:px-8 lg:px-10">
          <div className="max-w-6xl mx-auto">
            {/* Mobile: photo on top, text below */}
            <div className="flex flex-col rounded-3xl overflow-hidden md:hidden">
              <div className="rounded-t-3xl overflow-hidden aspect-[4/3]">
                <img 
                  src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80"
                  alt="Road trip"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-black/95 px-6 py-8 rounded-b-3xl">
                <h2 className="text-2xl font-light text-white mb-4">
                  Stop Gambling on Airfare
                </h2>
                <p className="text-white/70 font-normal mb-6 text-sm leading-relaxed">
                  Select a trip and start your payment plan today. If prices move, you're covered. Change your mind? Cancel anytime. Take control of your travel budget with scheduled payments.
                </p>
                <Button 
                  onClick={handleStartReserve}
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 rounded-full px-8"
                >
                  Start Reserve
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            {/* Desktop: image with overlay and text on top */}
            <div className="hidden md:block relative rounded-3xl overflow-hidden" style={{ aspectRatio: '21/9' }}>
              <img 
                src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80"
                alt="Road trip"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <div className="px-8 md:px-12 max-w-xl">
                  <h2 className="text-3xl md:text-4xl font-light mb-4">
                    Stop Gambling on Airfare
                  </h2>
                  <p className="text-white/70 font-normal mb-8">
                    Select a trip and start your payment plan today. If prices move, you're covered. Change your mind? Cancel anytime. Take control of your travel budget with scheduled payments.
                  </p>
                  <Button 
                    onClick={handleStartReserve}
                    size="lg"
                    className="bg-white text-black hover:bg-white/90 rounded-full px-8"
                  >
                    Start Reserve
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 px-6 md:px-8 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-light mb-4">
                Questions & Answers
              </h2>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="border border-white/10 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-normal">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-white/60 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-white/60 font-normal leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 px-6 md:px-8 lg:px-10 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            {/* Legal Disclosure */}
            <div className="mb-12">
              <p className="text-white/25 text-[11px] leading-relaxed">
                Defera Reserve is a closed-loop stored value program. Credits purchased through Reserve are non-refundable, non-transferable, and may not be redeemed for cash or cash equivalents. Reserve is not a bank account, deposit account, demand account, savings account, or any other type of account subject to FDIC or NCUA insurance. Credits do not accrue interest and have no cash value. Credits may only be applied toward eligible travel services offered through the Defera platform. All purchases are subject to Defera's Terms of Service. Pricing for travel services remains dynamic until the point of ticketing; Reserve does not guarantee, lock, or hold any fare, rate, or availability. Defera reserves the right to modify or discontinue Reserve at any time. For complete terms, see <Link to="/reserve/terms" className="underline hover:text-white/40 transition-colors">Defera Reserve Terms & Conditions</Link>.
              </p>
            </div>

            {/* Footer Links */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link to="/" className="flex items-center opacity-40 hover:opacity-60 transition-opacity">
                <img 
                  src="/defera-logo-web.png"
                  alt="Defera" 
                  className="h-5"
                />
              </Link>
              <div className="flex items-center gap-8 text-[11px] text-white/25">
                <Link to="/reserve/terms" className="hover:text-white/40 transition-colors">Reserve Terms</Link>
                <Link to="/terms" className="hover:text-white/40 transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="hover:text-white/40 transition-colors">Privacy Policy</Link>
                <Link to="/help" className="hover:text-white/40 transition-colors">Help Center</Link>
              </div>
              <p className="text-[11px] text-white/20">
                © {new Date().getFullYear()} Defera. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
